/**
 * @file PaymentStatusOverlay.tsx
 * @description Overlay component displaying payment status in tournament lobby
 *
 * Task 21.2: PaymentStatusOverlay component for lobby
 * Reference: .taskmaster/docs/task-21-payment-status-lobby.md Section 4.2
 */

import React from 'react';
import { PaymentStatus, StakePaymentSession, StakeSessionStatus } from '../../../types/payment';
import { EntryFee } from '../../../types/tournament';
import {
  OverlayWrapper,
  Message,
  SubMessage,
  Spinner,
  ErrorIconWrapper,
  WarningIconWrapper,
  InfoIconWrapper,
  ActionButton,
} from './styled';

export interface PaymentStatusOverlayProps {
  /** Current payment status (legacy) */
  paymentStatus: PaymentStatus | null;
  /** Whether this is a paid tournament */
  isPaidTournament: boolean;
  /** Entry fee configuration */
  entryFee: EntryFee | null;
  /** Callback for retry payment action */
  onRetryPayment: () => void;
  /** Callback for create new payment action */
  onCreateNewPayment: () => void;
  /** Callback for initiate payment action */
  onInitiatePayment: () => void;
  /** Unified payments: stake session with real-time status */
  stakeSession?: StakePaymentSession | null;
}

/**
 * PaymentStatusOverlay displays payment status and actions in the tournament lobby.
 * 
 * Rendering conditions:
 * - Does NOT render for FREE tournaments
 * - Does NOT render for CONFIRMED or USED payments
 * - Renders for PENDING, FAILED, EXPIRED, or null (no payment) statuses
 */
const PaymentStatusOverlay: React.FC<PaymentStatusOverlayProps> = ({
  paymentStatus,
  isPaidTournament,
  entryFee,
  onRetryPayment,
  onCreateNewPayment,
  onInitiatePayment,
  stakeSession,
}) => {
  // Don't render for FREE tournaments
  if (!isPaidTournament) {
    return null;
  }

  // Unified payments: use stakeSession status as source of truth if available
  const effectiveStatus: PaymentStatus | StakeSessionStatus | null = 
    stakeSession?.status ?? paymentStatus;

  // Don't render for confirmed payments (legacy: CONFIRMED/USED, unified: CONFIRMED_EXACT/OVERPAID)
  if (
    effectiveStatus === 'CONFIRMED' || 
    effectiveStatus === 'USED' ||
    effectiveStatus === 'CONFIRMED_EXACT' ||
    effectiveStatus === 'OVERPAID'
  ) {
    return null;
  }

  const formatEntryFee = (): string => {
    if (!entryFee) return '';
    return `${entryFee.amount} ${entryFee.currency}`;
  };

  // Format remaining amount for underpaid status
  const formatRemainingAmount = (): string => {
    if (!stakeSession) return '';
    const remaining = stakeSession.expectedAmount - stakeSession.paidAmount;
    return `${remaining.toFixed(2)} POL`;
  };

  const renderContent = () => {
    // Unified payments: handle stake session statuses
    if (stakeSession) {
      switch (stakeSession.status) {
        case 'PENDING':
          return (
            <>
              <Spinner data-testid="payment-pending-spinner" />
              <Message>Waiting for payment...</Message>
              <SubMessage>Entry fee: {formatEntryFee()}</SubMessage>
            </>
          );

        case 'UNDERPAID':
          return (
            <>
              <WarningIconWrapper>
                <span role="img" aria-label="warning">⚠️</span>
              </WarningIconWrapper>
              <Message>Payment received, but insufficient</Message>
              <SubMessage>
                Remaining to pay: {formatRemainingAmount()}
              </SubMessage>
            </>
          );

        case 'TIMEOUT':
          return (
            <>
              <ErrorIconWrapper>
                <span role="img" aria-label="error">⏰</span>
              </ErrorIconWrapper>
              <Message>Payment session expired</Message>
              <SubMessage>Your payment session has timed out. Please try again.</SubMessage>
              <ActionButton onClick={onCreateNewPayment}>
                New Payment
              </ActionButton>
            </>
          );

        case 'REFUND_INITIATED':
        case 'REFUNDED':
        case 'REFUND_FAILED':
          return (
            <>
              <InfoIconWrapper>
                <span role="img" aria-label="info">↩️</span>
              </InfoIconWrapper>
              <Message>Payment is being refunded</Message>
              <SubMessage>Please wait for the refund to complete.</SubMessage>
            </>
          );

        default:
          // For any other status, show generic payment required
          return (
            <>
              <InfoIconWrapper>
                <span role="img" aria-label="info">💳</span>
              </InfoIconWrapper>
              <Message>Payment required to join</Message>
              <SubMessage>Entry fee: {formatEntryFee()}</SubMessage>
              <ActionButton onClick={onInitiatePayment}>
                Pay & Join
              </ActionButton>
            </>
          );
      }
    }

    // Legacy flow: use paymentStatus
    switch (paymentStatus) {
      case 'PENDING':
        return (
          <>
            <Spinner data-testid="payment-pending-spinner" />
            <Message>Waiting for payment confirmation...</Message>
            <SubMessage>Entry fee: {formatEntryFee()}</SubMessage>
          </>
        );

      case 'FAILED':
        return (
          <>
            <ErrorIconWrapper>
              <span role="img" aria-label="error">❌</span>
            </ErrorIconWrapper>
            <Message>Payment failed</Message>
            <SubMessage>Your payment could not be processed. Please try again.</SubMessage>
            <ActionButton onClick={onRetryPayment}>
              Retry Payment
            </ActionButton>
          </>
        );

      case 'EXPIRED':
        return (
          <>
            <WarningIconWrapper>
              <span role="img" aria-label="warning">⚠️</span>
            </WarningIconWrapper>
            <Message>Payment expired</Message>
            <SubMessage>Your payment session has expired. Please create a new payment.</SubMessage>
            <ActionButton onClick={onCreateNewPayment}>
              New Payment
            </ActionButton>
          </>
        );

      case null:
      default:
        return (
          <>
            <InfoIconWrapper>
              <span role="img" aria-label="info">💳</span>
            </InfoIconWrapper>
            <Message>Payment required to join</Message>
            <SubMessage>Entry fee: {formatEntryFee()}</SubMessage>
            <ActionButton onClick={onInitiatePayment}>
              Pay & Join
            </ActionButton>
          </>
        );
    }
  };

  return (
    <OverlayWrapper data-testid="payment-status-overlay" role="alert">
      {renderContent()}
    </OverlayWrapper>
  );
};

export default PaymentStatusOverlay;
